/**
 * microservice接口配置
 */
import type { ApiEndpoint } from '../types';
import { API_CATEGORIES } from '../constants';

export const microserviceEndpoints: ApiEndpoint[] = [
  {
    id: "microservice-list",
    method: "GET",
    path: "/api/microservices",
    name: "获取微服务配置列表",
    description: "获取所有微服务的配置信息",
    category: API_CATEGORIES.MICROSERVICE,
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "array", description: "微服务配置列表" },
      { name: "data[].key", type: "string", description: "微服务标识" },
      { name: "data[].name", type: "string", description: "微服务名称" },
      { name: "data[].displayName", type: "string", description: "显示名称" },
      { name: "data[].url", type: "string", description: "前端访问URL" },
      { name: "data[].backendUrl", type: "string", description: "后端API URL" },
      { name: "data[].status", type: "string", description: "服务状态" },
      { name: "data[].permissions", type: "array", description: "所需权限" },
      { name: "data[].allowed_audience", type: "array", description: "令牌受众列表" },
      { name: "data[].has_token", type: "boolean", description: "是否有Token" },
      { name: "data[].token_info", type: "object", description: "Token信息" },
    ],
    responseExample: JSON.stringify({
      success: true,
      message: "查询成功",
      data: [
        {
          id: "1",
          key: "msadmin",
          name: "msadmin",
          displayName: "管理后台",
          url: "https://admin.ihoms.cn",
          backendUrl: "https://www.ihoms.cn/api",
          status: "online",
          permissions: ["msadmin:admins:view"],
          allowed_audience: ["msspace", "msmq"],
          has_token: true,
          token_info: {
            id: "1",
            token_value: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
            expires_at: "2025-12-31T23:59:59.000Z",
            is_active: true,
          },
        },
      ],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 5,
      },
    }, null, 2),
    queryParams: [
      { name: "current", type: "number", required: false, description: "当前页码", example: "1" },
      { name: "pageSize", type: "number", required: false, description: "每页条数", example: "10" },
      { name: "status", type: "string", required: false, description: "服务状态" },
      { name: "service_name", type: "string", required: false, description: "服务名称（模糊搜索）" },
      { name: "description", type: "string", required: false, description: "描述（模糊搜索）" },
    ],
  },
  {
    id: "microservice-stats",
    method: "GET",
    path: "/api/microservices/stats",
    name: "获取微服务统计信息",
    description: "获取微服务的统计信息",
    category: API_CATEGORIES.MICROSERVICE,
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "object", description: "统计信息" },
    ],
  },
  {
    id: "microservice-create",
    method: "POST",
    path: "/api/microservices",
    name: "创建微服务配置",
    description: "创建新的微服务配置",
    category: API_CATEGORIES.MICROSERVICE,
    bodyParams: [
      { name: "service_name", type: "string", required: true, description: "微服务名称" },
      { name: "display_name", type: "string", required: false, description: "显示名称" },
      { name: "service_description", type: "string", required: false, description: "服务描述" },
      { name: "service_endpoint", type: "string", required: false, description: "前端访问地址" },
      { name: "service_url", type: "string", required: false, description: "后端API地址" },
      { name: "health_check_url", type: "string", required: false, description: "健康检查地址" },
      { name: "allowed_audience", type: "array", required: false, description: "令牌受众列表", example: "[\"msspace\"]" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "object", description: "创建的微服务配置" },
    ],
  },
  {
    id: "microservice-detail",
    method: "GET",
    path: "/api/microservices/:name",
    name: "获取微服务配置详情",
    description: "根据名称获取微服务配置详情",
    category: API_CATEGORIES.MICROSERVICE,
    pathParams: [{ name: "name", type: "string", required: true, description: "微服务名称" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "object", description: "微服务配置详情" },
      { name: "data.allowed_audience", type: "array", description: "令牌受众列表" },
    ],
  },
  {
    id: "microservice-update",
    method: "PUT",
    path: "/api/microservices/:id",
    name: "更新微服务配置",
    description: "更新微服务配置信息",
    category: API_CATEGORIES.MICROSERVICE,
    pathParams: [{ name: "id", type: "string", required: true, description: "微服务ID" }],
    bodyParams: [
      { name: "service_name", type: "string", required: false, description: "微服务名称" },
      { name: "display_name", type: "string", required: false, description: "显示名称" },
      { name: "service_description", type: "string", required: false, description: "服务描述" },
      { name: "allowed_audience", type: "array", required: false, description: "令牌受众列表", example: "[\"msspace\"]" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "object", description: "更新后的微服务配置" },
    ],
  },
  {
    id: "microservice-delete",
    method: "DELETE",
    path: "/api/microservices/:id",
    name: "删除微服务配置",
    description: "根据ID删除微服务配置",
    category: API_CATEGORIES.MICROSERVICE,
    pathParams: [{ name: "id", type: "string", required: true, description: "微服务ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
  },
  {
    id: "microservice-batch-delete",
    method: "POST",
    path: "/api/microservices/batch-delete",
    name: "批量删除微服务配置",
    description: "根据ID列表批量删除微服务配置",
    category: API_CATEGORIES.MICROSERVICE,
    bodyParams: [{ name: "ids", type: "array", required: true, description: "微服务ID数组", example: "[\"1\", \"2\"]" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
  },
  {
    id: "microservice-health-check",
    method: "POST",
    path: "/api/microservices/:id/health-check",
    name: "微服务配置健康检查",
    description: "检查指定微服务配置的健康状态",
    category: API_CATEGORIES.MICROSERVICE,
    pathParams: [{ name: "id", type: "string", required: true, description: "微服务ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "object", description: "健康检查结果" },
    ],
  },
  {
    id: "microservice-refresh-menu",
    method: "POST",
    path: "/api/microservices/refresh-menu",
    name: "刷新菜单",
    description: "刷新微服务菜单配置",
    category: API_CATEGORIES.MICROSERVICE,
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
  },
];
